import { useCallback, useRef, useState } from "react";

/* The one rule the first-run screens share: a step advances only if the server
   said yes. Everything that could go wrong — a rejected call, a second Enter
   press while the first is still in flight — is handled here rather than four
   times over, so no screen can accidentally advance on a failure.

   `fn` does the advancing itself, after its awaits. If it throws, it never got
   there and the message lands under the footer. */
export function useSubmit() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Enter fires off the input, which is not disabled while pending — state
  // updates too late to stop a second call, a ref does not.
  const busy = useRef(false);

  const submit = useCallback(async (fn: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      console.error(e); // the footer shows the message; the console keeps the cause
      setError((e as Error)?.message || "Something went wrong");
    } finally {
      busy.current = false;
      setPending(false);
    }
  }, []);

  return { pending, error, setError, submit };
}
